import referring_step_1 from "@/assets/referring_step_1.svg";
import referring_step_2 from "@/assets/referring_step_2.svg";
import referring_step_3 from "@/assets/referring_step_3.svg";
import userTagIcon from "@/assets/user-tag.png";
import { Button } from "@/components/ui/button";
import { motion } from "motion/react";

export default function ReferringIsSimple({ setAuthModalOpen }: { setAuthModalOpen: (open: boolean) => void }) {
  const steps = [
    {
      number: "STEP 1:",
      title: "SUBMIT A REFERRAL",
      description: "Review jobs and add your referrals.",
      icon: referring_step_1,
      alt: "Submit a Referral"
    },
    {
      number: "STEP 2:",
      title: "TRACK THEIR PROGRESS", 
      description: "Track progress in real time.",
      icon: referring_step_2,
      alt: "Track Progress"
    },
    {
      number: "STEP 3:",
      title: "EARN REWARDS",
      description: "Get rewarded when your referral gets hired.",
      icon: referring_step_3,
      alt: "Earn Rewards"
    }
  ];

  return (
    <section className="bg-white relative pt-12 min-[375px]:pt-14 min-[425px]:pt-16 sm:pt-20 lg:pt-28 pb-12 min-[375px]:pb-14 min-[425px]:pb-16 sm:pb-20 lg:pb-24">
      <div className="flex flex-col w-full max-w-7xl mx-auto px-4 min-[375px]:px-6 min-[425px]:px-8 sm:px-6 lg:px-10">
        {/* Title Section */}
        <motion.div
          className="mb-12 min-[375px]:mb-14 min-[425px]:mb-16 lg:mb-20 text-center lg:text-left"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <h2 className="text-2xl min-[375px]:text-3xl min-[425px]:text-4xl sm:text-4xl md:text-5xl lg:text-[60px] xl:text-[64px] font-bold text-[#2d2d2d] uppercase space-grotesk leading-[120%] mb-4 min-[375px]:mb-5 lg:mb-6">
            Referring is simple
          </h2>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-6 xl:gap-8 mb-8 min-[375px]:mb-10 min-[425px]:mb-12 lg:mb-16 place-items-center lg:place-items-start">
          {steps.map((step, index) => {
            // Different rotation angles for each step to match mockup
            const containerRotations = [
              "rotate-[15deg]", // Step 1 - right tilt
              "rotate-[-15deg]",  // Step 2 - left tilt  
              "rotate-[20deg]"  // Step 3 - right tilt
            ];
            
            // Counter-rotate icons to keep them upright relative to container
            const iconRotations = [
              "rotate-[-15deg]", // Counter-rotate Step 1 icon
              "rotate-[15deg]",   // Counter-rotate Step 2 icon
              "rotate-[-20deg]"   // Counter-rotate Step 3 icon
            ];
            
            return (
              <motion.div
                key={index}
                className="flex flex-col items-center lg:items-start w-full max-w-[280px] min-[375px]:max-w-[300px] min-[425px]:max-w-[310px] lg:max-w-none"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
              >
                {/* Blue Icon Box */}
                <div className="relative mb-4 min-[375px]:mb-5 min-[425px]:mb-6 lg:mb-8">
                  <div className={`w-20 h-20 min-[375px]:w-24 min-[375px]:h-24 min-[425px]:w-28 min-[425px]:h-28 sm:w-28 sm:h-28 lg:w-28 lg:h-28 bg-gradient-to-br from-[#4B6EF5] to-[#3B82F6] rounded-xl min-[375px]:rounded-2xl lg:rounded-3xl flex items-center justify-center shadow-lg transform transition-transform hover:scale-105 ${containerRotations[index]}`}>
                    <img 
                      src={step.icon} 
                      alt={step.alt}
                      className={`w-12 h-12 min-[375px]:w-14 min-[375px]:h-14 min-[425px]:w-16 min-[425px]:h-16 sm:w-16 sm:h-16 lg:w-18 lg:h-18 transform ${iconRotations[index]}`}
                    />
                  </div>
                </div>

                {/* Step Content */}
                <div className="max-w-sm text-center lg:text-left">
                  <div className="mb-2 min-[375px]:mb-3 lg:mb-4">
                    <p className="text-xs min-[375px]:text-sm min-[425px]:text-base sm:text-base lg:text-lg font-medium text-[#666666] uppercase tracking-wide mb-1 min-[375px]:mb-2">
                      {step.number}
                    </p>
                    <h3 className="text-base min-[375px]:text-lg min-[425px]:text-xl sm:text-xl lg:text-2xl font-bold text-[#2d2d2d] uppercase space-grotesk leading-[130%]">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-sm min-[375px]:text-base min-[425px]:text-base sm:text-base lg:text-lg text-[#666666] leading-[140%] text-balance">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Button */}
        <motion.div
          className="flex justify-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <Button
            variant="primary"
            size="primary"
            className="w-full max-w-[280px] min-[375px]:max-w-[320px] min-[425px]:max-w-[345px]"
            onClick={() => setAuthModalOpen(true)}
          >
            <span>See Open Roles to Refer</span>
            <img
              src={userTagIcon}
              alt="User tag"
              className="w-4 min-[375px]:w-5 min-[425px]:w-6 h-4 min-[375px]:h-5 min-[425px]:h-6 brightness-0 invert"
            />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
